import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useMemo, useRef, useState } from 'react'

import {
   Avatar,
   Button,
   colors,
   Hierarchy,
   SubVariant,
   TextArea
} from '@flowwai-frontend/design-system'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { ENTER_KEY_VALUE } from '@flowwai-frontend/shared/constants'
import {
   Edit03Icon,
   EllipseIcon,
   User01Icon
} from '@flowwai-frontend/design-system/icons'
import { theme } from '../../theme'

import { timeAgo } from '../../utils'

// import UserActionButtons from './UserActionButtons'
import * as Styles from './styles'

interface Props {
   userSearchQuery: string
   onUpdateSearchQuery: (searchQuery: string) => void
   isQueryEditable?: boolean
   timeStamp: Date
}

const UserQueriedChatBox = (props: Props): React.ReactElement => {
   const { userSearchQuery, onUpdateSearchQuery, isQueryEditable } = props
   const t = useCustomT('aiChatSupport:userQueriedChatBox')
   const userQueriedChatBoxTheme = theme.userQueriedChatBox

   const [isHovered, setIsHovered] = useState<boolean>(false)
   const [isTextInEditableState, setIsTextInEditableState] = useState<boolean>(
      false
   )
   const [searchQuery, setSearchQuery] = useState<string>(userSearchQuery)
   const latestQuery = useRef<string>(userSearchQuery)

   const handleMouseEnter = (): void => setIsHovered(true)
   const handleMouseLeave = (): void => setIsHovered(false)

   const onClickTrigger = () => {
      setIsTextInEditableState(true)
   }

   const onClickCancel = () => {
      setSearchQuery(latestQuery.current)
      setIsTextInEditableState(false)
   }

   const onClickSend = () => {
      const trimmedQuery = searchQuery.trim()
      latestQuery.current = trimmedQuery

      setIsTextInEditableState(false)
      setIsHovered(false)

      setSearchQuery(trimmedQuery)

      onUpdateSearchQuery(trimmedQuery)
   }

   const onKeyDown = (e: React.KeyboardEvent): void => {
      const isEnterKey = e.key === ENTER_KEY_VALUE

      const isShiftAndEnterKey = e.shiftKey && isEnterKey

      if (isShiftAndEnterKey) return

      if (isEnterKey) {
         e.preventDefault()
         onClickSend()
      }
   }

   const isSendButtonDisabled = useMemo(() => {
      if (latestQuery.current === searchQuery.trim()) return true
      return false
   }, [searchQuery, latestQuery.current])

   const renderUserQuery = () => (
      <div className={Styles.userSearchQueryContainerStyles}>
         <TextArea
            size={'ExtraSmall'}
            label={''}
            value={searchQuery}
            onChange={setSearchQuery}
            inputClassName={cn(
               Styles.userSearchQueryTextStyles,
               userQueriedChatBoxTheme.userSearchQueryTextColor,
               userQueriedChatBoxTheme.defaultBgColor
            )}
            inputContainerClassName={cn(
               Styles.textAreaContainerStyles,
               userQueriedChatBoxTheme.defaultBgColor
            )}
            isReadOnly={!isTextInEditableState}
            isDisabled={!isTextInEditableState}
            autoFocus={isTextInEditableState}
            onKeyDown={onKeyDown}
         />
         {/* TODO: uncomment to add edit button */}
         {/* {isTextInEditableState ? (
            <UserActionButtons
               onClickCancel={onClickCancel}
               onClickSubmit={onClickSend}
               isSubmitButtonDisabled={isSendButtonDisabled}
            />
         ) : null} */}
      </div>
   )

   const renderTime = () => (
      <p
         className={cn(
            'text-sm-medium  flex flex-row gap-2 items-center',
            userQueriedChatBoxTheme.nameTextColor
         )}
      >
         You <EllipseIcon />
         <span className={cn(userQueriedChatBoxTheme.timeTextColor)}>
            {timeAgo(props.timeStamp, t)}
         </span>
      </p>
   )

   const renderEditButton = () =>
      isQueryEditable && !isTextInEditableState && isHovered ? (
         <Button
            hierarchy={Hierarchy.Secondary}
            subVariant={SubVariant.GrayOutline}
            size={'Small'}
            onClick={onClickTrigger}
            leftIcon={({ stroke }) => (
               <Edit03Icon width={20} height={20} stroke={stroke} />
            )}
            className={Styles.editButtonStyles}
         >
            {t('edit')}
         </Button>
      ) : null

   const renderQueryBox = () => (
      // <div
      //    className={cn(
      //       Styles.queryContainerStyles,
      //       userQueriedChatBoxTheme.defaultBgColor,
      //       isTextInEditableState
      //          ? cn(
      //               userQueriedChatBoxTheme.activeCardBorderColor,
      //               'border-[2px]'
      //            )
      //          : userQueriedChatBoxTheme.cardBorderColor
      //    )}
      // >
      // <div className={Styles.querySubContainerStyles}>
      <div
         className={cn(
            Styles.queryHeaderStyles,
            userQueriedChatBoxTheme.questionBorderColor,
            userQueriedChatBoxTheme.questionBGColor
         )}
      >
         {/* {renderTime()} */}
         <p
            className={cn(
               Styles.nameTextStyles,
               userQueriedChatBoxTheme.messageContentTextColor
            )}
         >
            {searchQuery}
         </p>
      </div>
      // </div>
      // {renderEditButton()}
      // </div>
   )

   const renderAvatar = () => (
      <Avatar
         name={''}
         type={'Icon'}
         shape={'Circular'}
         size={'Small'}
         userIcon={() => (
            <User01Icon
               height={20}
               width={20}
               fill={userQueriedChatBoxTheme.userIconFillColor}
            />
         )}
      />
   )

   return (
      <div
         className={Styles.containerStyles}
         onMouseEnter={handleMouseEnter}
         onMouseLeave={handleMouseLeave}
      >
         {renderQueryBox()}
         {renderAvatar()}
      </div>
   )
}

export default observer(UserQueriedChatBox)
